import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone;

const toBytes = (v) => {
  const raw = atob((v + "=".repeat((4 - (v.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const getSub = async () => (await navigator.serviceWorker.register("/sw.js", { scope: "/" })).pushManager.getSubscription();

export const ClipPushToggle = () => {
  const { t } = useI18n();
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const ok = supported();
  useEffect(() => {
    if (!ok) return;
    getSub().then((s) => { setOn(!!s); if (s) api.post("/push/subscriptions", s.toJSON()).catch(() => {}); }).catch(() => setOn(false));
  }, [ok]);

  const toggle = async (next) => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      if (next) {
        if ((await Notification.requestPermission()) !== "granted") throw new Error(t("push.denied"));
        const { data } = await api.get("/push/public-key");
        const s = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toBytes(data.key) });
        await api.post("/push/subscriptions", s.toJSON());
        await api.post("/push/test");
        toast.success(t("push.on"));
      } else {
        const s = await reg.pushManager.getSubscription();
        if (s) { await api.post("/push/unsubscribe", { endpoint: s.endpoint }); await s.unsubscribe(); }
        toast.success(t("push.off"));
      }
      setOn(next);
    } catch (e) { toast.error(e.response ? formatApiError(e) : e.message === t("push.denied") ? e.message : t("push.failed")); }
    finally { setBusy(false); }
  };

  const ios = isIOS() && !standalone();
  return (
    <div className="cx-card rounded-2xl p-5 flex items-start gap-4" data-testid="clip-push-card">
      <BellRing className="w-5 h-5 text-[#3ad7ff] mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display text-white">{t("push.title")}</div>
        <p className="text-sm text-slate-400 mt-1">{t("push.desc")}</p>
        {ios && <p className="text-xs text-amber-300 mt-2" data-testid="clip-push-ios-hint">{t("push.ios")}</p>}
        {!ok && !ios && <p className="text-xs text-slate-500 mt-2" data-testid="clip-push-unsupported">{t("push.unsupported")}</p>}
      </div>
      <Switch checked={!!on} disabled={!ok || busy || on === null} onCheckedChange={toggle} aria-label={t("push.title")} data-testid="clip-push-switch" />
    </div>
  );
};
